import {
  DataGrid,
  Dialog,
  FilterToolbar,
  formatDate,
  formatJson,
  Pre,
  StatusBadge,
  type FilterDef,
  type GridQuery,
  type Severity
} from '@ijoin/components';
import {
  AdminActivityRowRes,
  GetAdminActivityParams,
  useGetAdminActivity,
  useGetAdminAdvisoryOrgActivity,
  useGetAutoJobQueueStatuses,
  useGetAutoJobQueueTaskTypes
} from '@ijoin/hooks';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';

import { FC, useState } from 'react';

import { LinkButton } from './LinkButton';
import { useAdvisoryOrganizationOptions } from './useAdvisoryOrganizationOptions';
import { usePlanOptions } from './usePlanOptions';

const STATUS_SEVERITY: Record<string, Severity> = {
  C: 'success',
  E: 'error',
  I: 'info',
  P: 'info',
  R: 'info',
  S: 'success',
  W: 'warning'
};

const formatDateTime = (value: unknown) =>
  value ? formatDate(value as string, { format: 'MM/DD/YYYY h:mm:ss A' }) : '';

type ActivityMonitorProps =
  | { advisoryOrganizationId: string; mode: 'org' }
  | { advisoryOrganizationId?: undefined; mode: 'global' };

export const ActivityMonitorTab: FC<ActivityMonitorProps> = props => {
  const [selected, setSelected] = useState<AdminActivityRowRes | null>(null);
  const [query, setQuery] = useState<GridQuery>();

  const search = useSearch({ strict: false }) as {
    advisoryOrganizationId?: string;
    jobStatus?: string;
    planId?: string;
    queueDateTimeFrom?: string;
    queueDateTimeTo?: string;
    taskTypeId?: string;
  };

  const [draftOrgId, setDraftOrgId] = useState<string | undefined>();

  const appliedOrgId =
    props.mode === 'org'
      ? props.advisoryOrganizationId
      : (draftOrgId ??
        (query as GetAdminActivityParams | undefined)?.advisoryOrganizationId);

  const taskTypes = useGetAutoJobQueueTaskTypes();
  const statuses = useGetAutoJobQueueStatuses();

  const orgs = useAdvisoryOrganizationOptions(props.mode === 'global');
  const plans = usePlanOptions(appliedOrgId);

  const onCloseDetailsDialog = () => setSelected(null);

  const taskTypeOptions = (taskTypes.data ?? [])
    .filter(t => t.taskTypeId)
    .map(t => ({
      label: t.description ?? t.taskTypeId,
      value: t.taskTypeId
    }));

  const statusOptions = (statuses.data ?? [])
    .filter(s => s.jobStatusCode)
    .map(s => ({
      label: s.description ?? s.jobStatusCode,
      value: s.jobStatusCode
    }));

  const filters: FilterDef[] = [
    ...(props.mode === 'global'
      ? [
          {
            isLoading: orgs.isLoading,
            label: 'Advisory Organization',
            name: 'advisoryOrganizationId',
            options: orgs.data ?? [],
            type: 'select' as const
          }
        ]
      : []),
    {
      isLoading: taskTypes.isLoading,
      label: 'Activity Type',
      name: 'taskTypeId',
      options: taskTypeOptions,
      type: 'select'
    },
    {
      ...plans,
      label: 'Plan',
      name: 'planId',
      type: 'autocomplete'
    },
    { label: 'External Plan ID', name: 'externalPlanId', type: 'text' },
    { label: 'First Name', name: 'firstName', type: 'text' },
    { label: 'Last Name', name: 'lastName', type: 'text' },
    {
      isLoading: statuses.isLoading,
      label: 'Status',
      name: 'jobStatusCode',
      options: statusOptions,
      type: 'multiSelect'
    },
    {
      endName: 'endQueueDateTime',
      label: 'Queued',
      startName: 'startQueueDateTime',
      type: 'dateRange'
    },
    {
      endName: 'endJobStartDateTime',
      label: 'Processing Start',
      startName: 'startJobStartDateTime',
      type: 'dateRange'
    },
    {
      endName: 'endJobEndDateTime',
      label: 'Processing End',
      startName: 'startJobEndDateTime',
      type: 'dateRange'
    }
  ];

  const orgActivity = useGetAdminAdvisoryOrgActivity(
    props.advisoryOrganizationId ?? '',
    query as GetAdminActivityParams,
    {
      query: {
        enabled: props.mode === 'org' && !!query,
        placeholderData: keepPreviousData
      }
    }
  );

  const globalActivity = useGetAdminActivity(query as GetAdminActivityParams, {
    query: {
      enabled: props.mode === 'global' && !!query,
      placeholderData: keepPreviousData
    }
  });

  const activity = props.mode === 'org' ? orgActivity : globalActivity;

  const orgColumn: GridColDef<AdminActivityRowRes> = {
    field: 'advisoryOrganizationName',
    filterable: false,
    flex: 1,
    headerName: 'Advisory Organization',
    minWidth: 200,
    renderCell: params =>
      params.row.advisoryOrganizationId ? (
        <LinkButton
          params={{
            advisoryOrganizationId: params.row.advisoryOrganizationId
          }}
          to='/organizations/$advisoryOrganizationId/activity-monitor'
          variant='text'>
          {params.row.advisoryOrganizationName ??
            params.row.advisoryOrganizationId}
        </LinkButton>
      ) : (
        (params.row.advisoryOrganizationName ?? '')
      ),
    sortable: false,
    type: 'string'
  };

  const columns: GridColDef<AdminActivityRowRes>[] = [
    {
      align: 'center',
      disableColumnMenu: true,
      display: 'flex',
      field: 'actions',
      filterable: false,
      headerName: '',
      renderCell: params => (
        <IconButton
          aria-label='Details'
          data-testid={`details-${params.row.jobId}`}
          onClick={() => setSelected(params.row)}>
          <InfoOutlinedIcon />
        </IconButton>
      ),
      sortable: false,
      width: 65
    },
    ...(props.mode === 'global' ? [orgColumn] : []),
    {
      field: 'taskTypeId',
      filterable: false,
      flex: 1,
      headerName: 'Activity Type',
      minWidth: 220,
      renderCell: params => params.row.taskType ?? params.row.taskTypeId,
      type: 'string'
    },
    {
      field: 'planName',
      filterable: false,
      flex: 1,
      headerName: 'Plan',
      minWidth: 220,
      renderCell: params =>
        params.row.planId && params.row.advisoryOrganizationId ? (
          <LinkButton
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId,
              planId: params.row.planId
            }}
            to='/organizations/$advisoryOrganizationId/plans/$planId'
            variant='text'>
            {params.row.planName ?? ''}
          </LinkButton>
        ) : (
          (params.row.planName ?? '')
        ),
      sortable: false,
      type: 'string'
    },
    {
      field: 'externalPlanId',
      filterable: false,
      flex: 0.75,
      headerName: 'External Plan ID',
      minWidth: 180,
      renderCell: params => params.row.externalPlanId ?? '',
      sortable: false,
      type: 'string'
    },
    {
      field: 'firstName',
      flex: 0.75,
      headerName: 'First Name',
      minWidth: 150,
      renderCell: params =>
        params.row.firstName &&
        params.row.planEnrollmentId &&
        params.row.advisoryOrganizationId &&
        params.row.planId ? (
          <LinkButton
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId,
              planEnrollmentId: params.row.planEnrollmentId,
              planId: params.row.planId
            }}
            to='/organizations/$advisoryOrganizationId/plans/$planId/participants/$planEnrollmentId'
            variant='text'>
            {params.row.firstName}
          </LinkButton>
        ) : (
          (params.row.firstName ?? '')
        ),
      type: 'string'
    },
    {
      field: 'lastName',
      flex: 0.75,
      headerName: 'Last Name',
      minWidth: 150,
      renderCell: params =>
        params.row.lastName &&
        params.row.planEnrollmentId &&
        params.row.advisoryOrganizationId &&
        params.row.planId ? (
          <LinkButton
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId,
              planEnrollmentId: params.row.planEnrollmentId,
              planId: params.row.planId
            }}
            to='/organizations/$advisoryOrganizationId/plans/$planId/participants/$planEnrollmentId'
            variant='text'>
            {params.row.lastName}
          </LinkButton>
        ) : (
          (params.row.lastName ?? '')
        ),
      type: 'string'
    },
    {
      field: 'jobStatusCode',
      filterable: false,
      flex: 0.75,
      headerName: 'Status',
      minWidth: 160,
      renderCell: params => (
        <StatusBadge
          severity={STATUS_SEVERITY[params.value as string] ?? 'info'}>
          {params.row.jobStatus ?? (params.value as string)}
        </StatusBadge>
      ),
      type: 'string'
    },
    {
      field: 'queueDateTime',
      flex: 0.75,
      headerName: 'Queued',
      minWidth: 170,
      type: 'date',
      valueFormatter: formatDateTime,
      valueGetter: (_value, row) =>
        row.queueDateTime ? new Date(row.queueDateTime) : null
    },
    {
      field: 'jobStartDateTime',
      flex: 0.75,
      headerName: 'Processing Start',
      minWidth: 170,
      type: 'date',
      valueFormatter: formatDateTime,
      valueGetter: (_value, row) =>
        row.jobStartDateTime ? new Date(row.jobStartDateTime) : null
    },
    {
      field: 'jobEndDateTime',
      flex: 0.75,
      headerName: 'Processing End',
      minWidth: 170,
      type: 'date',
      valueFormatter: formatDateTime,
      valueGetter: (_value, row) =>
        row.jobEndDateTime ? new Date(row.jobEndDateTime) : null
    }
  ];

  return (
    <Stack sx={{ flex: 1, minHeight: 0, width: '100%' }}>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filters}
        getRowId={row => row.jobId}
        initialFilters={{
          ...(props.mode === 'global' &&
            search.advisoryOrganizationId && {
              advisoryOrganizationId: search.advisoryOrganizationId
            }),
          ...(search.jobStatus && {
            jobStatusCode: search.jobStatus
              .split(',')
              .map(c => c.trim())
              .filter(Boolean)
          }),
          ...(search.planId && { planId: search.planId }),
          ...(search.queueDateTimeFrom && {
            startQueueDateTime: search.queueDateTimeFrom
          }),
          ...(search.queueDateTimeTo && {
            endQueueDateTime: search.queueDateTimeTo
          }),
          ...(search.taskTypeId && { taskTypeId: search.taskTypeId })
        }}
        initialSort={[{ field: 'queueDateTime', sort: 'desc' }]}
        loading={!query || activity.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSizeOptions={[25, 50, 100]}
        paginationMode='server'
        pinnedColumns={{
          left: [
            'actions',
            ...(props.mode === 'global' ? ['advisoryOrganizationName'] : []),
            'taskTypeId'
          ]
        }}
        rowCount={activity.data?.pagination?.total ?? 0}
        rows={activity.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'activityMonitor'
          },
          toolbar: {
            onDraftChange: values =>
              setDraftOrgId(
                (values.advisoryOrganizationId as string) || undefined
              ),
            onRefresh: activity.refetch,
            searchButtonTestId: 'activityMonitorSearch',
            triggerTestId: 'activityMonitorFilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />

      <Dialog
        data-testid='activityDetails'
        fullWidth
        isOpen={!!selected}
        maxWidth='md'
        onClose={onCloseDetailsDialog}>
        <DialogTitle
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
          <Typography component='span' variant='h4'>
            {selected?.taskType ?? selected?.taskTypeId}
          </Typography>
          <IconButton
            data-testid='closeActivityDetails'
            onClick={onCloseDetailsDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom my={2} variant='overline'>
            Job Info
          </Typography>
          <Paper variant='outlined'>
            <Pre>
              {selected?.jobBody ? formatJson(selected.jobBody) : 'No job info'}
            </Pre>
          </Paper>

          <Typography gutterBottom sx={{ mt: 3 }} variant='overline'>
            Log Info
          </Typography>
          <Paper variant='outlined'>
            <Pre>
              {selected?.logInfo ? formatJson(selected.logInfo) : 'No log info'}
            </Pre>
          </Paper>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

ActivityMonitorTab.displayName = 'ActivityMonitorTab';
